import React, { useState } from "react";
import {
  Button,
  Checkbox,
  DropdownItemProps,
  Form,
  FormField,
  FormGroup,
  Grid,
  Message,
  Modal,
  Progress,
} from "semantic-ui-react";
import { PostServices } from "@/services";
import * as DTO from "../../../../interfaces";
import { ComplianceExportEntry, ReviewStatus, SourceType } from "@/interfaces";
import { exportComplianceEntries, exportComplianceEntriesToCsv, useSocialCoachSession } from "../../../../utils";
import moment from "moment";
import { CustomDropDownComponent } from "@/components/CustomDropDown";
import { DateRangePicker } from "@/components/DateRangePicker";
import { mediaTypeOptions, reviewStatusOptions, sourceTypeOptions } from "../options";
import "./styles.scss";

interface ComplianceExportModalProps {
  open: boolean;
  onClose: () => void;

  players: { [key: string]: DTO.SubscribedPlayerSearchResponse };
  playerOptions: DropdownItemProps[];
  segmentationOptions: DropdownItemProps[];
}

type Props = ComplianceExportModalProps;

export const ComplianceExportModal: React.FC<Props> = ({
  open,
  onClose,
  players,
  playerOptions,
  segmentationOptions,
}) => {
  const { coachId } = useSocialCoachSession();
  const today = new Date();
  const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());

  const [fromDate, setFromDate] = useState<Date | undefined>(lastMonth);
  const [toDate, setToDate] = useState<Date | undefined>(today);
  const [selectedSegmentations, setSelectedSegmentations] = useState<string[]>([]);
  const [selectedReviewStatuses, setSelectedReviewStatuses] = useState<ReviewStatus[]>([]);
  const [selectedSourceTypes, setSelectedSourceTypes] = useState<SourceType[]>([]);
  const [selectedMediaCategory, setSelectedMediaCategory] = useState<DTO.MediaCategory | undefined>(undefined);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<number[]>([]);
  const [flaggedOnly, setFlaggedOnly] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [exporting, setExporting] = useState<boolean>(false);
  const [controller, setController] = React.useState<AbortController>();
  const [exportError, setExportError] = useState<string | undefined>();

  const handleClose = () => {
    if (controller) {
      controller.abort();
    }
    setExporting(false);
    onClose();
  };

  const exportData = async () => {
    setExportError(undefined);
    const localController = new AbortController();
    setController(localController);
    let exportPage = 0;
    let lastPage = false;
    let totalPages = 1;
    let entries: ComplianceExportEntry[] = [];

    setProgress(0);
    setExporting(true);
    while (!lastPage && !localController.signal.aborted) {
      try {
        const data = await PostServices.complianceExport(
          coachId!,
          {
            playerIds: selectedPlayerIds,
            keyphrasePolicy: flaggedOnly ? "FLAG" : undefined,
            reviewStatuses: selectedReviewStatuses,
            segmentationDetailIds: selectedSegmentations,
            sourceTypes: selectedSourceTypes,
            mediaCategory: selectedMediaCategory,
            from: fromDate,
            until: toDate,
          },
          exportPage,
          100
        );
        lastPage = data.last || false;
        entries = entries.concat(data.content);
        totalPages = data.totalPages || 1;
      } catch (e) {
        console.error(e);
        setExportError(
          "There was an error exporting the compliance data. Some data may be incomplete. If the problem persists, please contact support."
        );
      } finally {
        setProgress((exportPage / totalPages) * 100);
        exportPage++;
      }
    }

    if (!localController.signal.aborted) {
      const data: DTO.ExportComplianceEntryObject[] = exportComplianceEntries(entries, players);
      if (data.length > 0) {
        const from = moment(fromDate).format("MM/DD/YY");
        const until = moment(toDate).format("MM/DD/YY");
        exportComplianceEntriesToCsv(`SocialCoach Compliance Export - ${from} - ${until}`, data);
      } else {
        setExportError(
          "No data was found to export. Please try again with different filters or select a different date range."
        );
      }
    }

    setExporting(false);
  };

  return (
    <Modal open={open} onClose={handleClose} className={"complianceExportModal"}>
      <Modal.Header>Export Compliance Data</Modal.Header>
      <Modal.Content>
        {exportError && exportError.length > 0 && (
          <Grid.Row columns={1}>
            <Message floating warning list={[exportError]} />
          </Grid.Row>
        )}
        {exporting && (
          <>
            <Progress percent={progress} label={`Exporting compliance data...`} />
          </>
        )}
        {!exporting && (
          <>
            <Form>
              <FormGroup widths="equal">
                <Form.Field>
                  <label>Segmentations</label>
                  <CustomDropDownComponent
                    options={segmentationOptions}
                    placeholder={"Select segmentations"}
                    onChangeHandler={setSelectedSegmentations}
                    defaultValue={selectedSegmentations}
                    multiple={true}
                  />
                </Form.Field>
                <Form.Field>
                  <label>Players</label>
                  <CustomDropDownComponent
                    options={playerOptions}
                    placeholder={"Select players"}
                    onChangeHandler={setSelectedPlayerIds}
                    defaultValue={selectedPlayerIds}
                    multiple={true}
                  />
                </Form.Field>
              </FormGroup>

              <FormGroup widths="equal">
                <Form.Field>
                  <label>Source Types</label>
                  <CustomDropDownComponent
                    options={sourceTypeOptions}
                    placeholder={"Select source types"}
                    onChangeHandler={setSelectedSourceTypes}
                    defaultValue={selectedSourceTypes}
                    multiple={true}
                  />
                </Form.Field>

                <Form.Field>
                  <label>Media Type</label>
                  <CustomDropDownComponent
                    options={mediaTypeOptions}
                    placeholder={"Select media type"}
                    defaultValue={selectedMediaCategory}
                    onChangeHandler={setSelectedMediaCategory}
                    multiple={false}
                  />
                </Form.Field>
              </FormGroup>

              <FormGroup widths="equal">
                <Form.Field>
                  <label>Review Statuses</label>
                  <CustomDropDownComponent
                    options={reviewStatusOptions}
                    placeholder={"Select review statuses"}
                    onChangeHandler={setSelectedReviewStatuses}
                    defaultValue={selectedReviewStatuses}
                    multiple={true}
                  />
                </Form.Field>

                <FormField>
                  <label>Flagged Posts Only</label>
                  <Checkbox
                    checked={flaggedOnly}
                    onChange={(_, d) => {
                      setFlaggedOnly(d.checked as boolean);
                    }}
                  />
                </FormField>
              </FormGroup>

              <FormGroup widths="equal">
                <FormField>
                  <label>Date Range</label>
                  <DateRangePicker
                    startDate={fromDate}
                    endDate={toDate}
                    onChangeStartDate={setFromDate}
                    onChangeEndDate={setToDate}
                    maxDate={new Date()}
                  />
                </FormField>
              </FormGroup>
            </Form>
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleClose} secondary>
          Cancel
        </Button>
        <Button onClick={exportData} loading={exporting} primary>
          Start Export
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
